import React, {ReactElement, ReactNode} from "react";

import ImageVideoTextStackedSingleColumn from "./single-column/ImageVideoTextStackedSingleColumn";
import ImageVideoTextStackedTwoColumns from "./two-columns/ImageVideoTextStackedTwoColumns";
import {
  constructWistiaVideoEmbedUrlById,
  parseContentfulWistiaVideoJSON,
} from "/src/utils/wistia";
import {ImageObject} from "../image/Image";
import {CTA} from "../buttons/cta-button/CTAButton";
import {VideoType} from "../videos/video/Video";

export interface ImageVideoTextStackedProps
  extends Queries.ImageVideoTextStackedFragmentFragment {
  transparentBg?: boolean;
  featuredComponent?: ReactNode;
}

const ImageVideoTextStacked = (
  props: ImageVideoTextStackedProps
): ReactElement => {
  const {
    title,
    body,
    eyebrow,
    eyebrowLogo,
    image,
    mobileImage,
    videoSource,
    wistiaVideoSource,
    videoThumbnail,
    secondVideoSource,
    wistiaSecondVideoSource,
    secondVideoThumbnail,
    secondImage,
    ctaButton,
    callout,
    videoBehavior,
    theme,
    transparentBg,
    featuredComponent,
    videoControls = false,
    anchorLinkId,
  } = props;

  // GROW-1503, we should check if firstImage/videoSrc + secondImage/secondVideoSource is not null/valid to decide
  // whether or not to use double image layout, as the layout field should no longer be used
  let isDoubleImage: boolean = false;

  // GROW-3265, when wistiaVideoSource present, we will prioritise that instead
  let firstVideo: string = videoSource!;

  let secondVideo: string = secondVideoSource!;

  if (wistiaVideoSource) {
    const {internal} = wistiaVideoSource;
    const {content} = internal;
    const wistiaContent = parseContentfulWistiaVideoJSON(content, true);

    if (wistiaContent) {
      // eslint-disable-next-line camelcase
      firstVideo = constructWistiaVideoEmbedUrlById(wistiaContent.hashed_id);
    }
  }

  if (wistiaSecondVideoSource) {
    const {internal} = wistiaSecondVideoSource;
    const {content} = internal;
    const wistiaContent = parseContentfulWistiaVideoJSON(content, true);

    if (wistiaContent) {
      // eslint-disable-next-line camelcase
      secondVideo = constructWistiaVideoEmbedUrlById(wistiaContent.hashed_id);
    }
  }

  if ((firstVideo || image) && (secondImage || secondVideo)) {
    isDoubleImage = true;
  }

  const darkMode = theme === "dark";

  if (isDoubleImage) {
    return (
      <ImageVideoTextStackedTwoColumns
        eyebrowImg={eyebrowLogo as ImageObject}
        eyebrow={eyebrow!}
        heading={title!}
        copy={body?.body!}
        image={image as ImageObject}
        cta={ctaButton as CTA}
        darkMode={darkMode}
        transparentBg={transparentBg}
        videoSrc={firstVideo}
        secondVideoSrc={secondVideo}
        secondImage={secondImage as ImageObject}
        videoThumbnail={videoThumbnail as ImageObject}
        secondVideoThumbnail={secondVideoThumbnail as ImageObject}
        videoBehavior={videoBehavior as VideoType}
        videoControls={videoControls as boolean}
        anchorLinkId={anchorLinkId as string}
      />
    );
  }

  return (
    <ImageVideoTextStackedSingleColumn
      eyebrow={eyebrow!}
      eyebrowImg={eyebrowLogo as ImageObject}
      heading={title!}
      copy={body?.body!}
      image={image as ImageObject}
      mobileImage={mobileImage as ImageObject}
      cta={ctaButton as CTA}
      callout={{
        headline: callout?.copy?.copy!,
        image: callout?.thumbnail as ImageObject,
        cta: callout?.ctaLink as CTA,
      }}
      darkMode={darkMode}
      transparentBg={transparentBg}
      videoSrc={firstVideo}
      videoThumbnail={videoThumbnail as ImageObject}
      featuredComponent={featuredComponent}
      videoBehavior={videoBehavior as VideoType}
      videoControls={videoControls as boolean}
      anchorLinkId={anchorLinkId as string}
    />
  );
};

export default ImageVideoTextStacked;
